import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { LOT_ACQUISITION_TEMPLATE, SPEC_BUILD_TEMPLATE, CUSTOM_HOME_TEMPLATE } from '../constants'
import { makeTasksFromTemplate, conflictCheckedUpdate } from '../utils'
import { getTierLimits } from '../tierLimits'
import { trackEvent } from '../analytics'

function mapTask(t, costData) {
  const taskCosts = (costData || []).filter(c => c.task_id === t.id).map(c => ({
    id: c.id, supabaseId: c.id, taskId: c.task_id, projectId: c.project_id,
    category: c.category || "Other", description: c.description || "",
    estimated: c.estimated != null ? Number(c.estimated) : 0,
    actual: c.actual != null ? Number(c.actual) : 0,
    updatedAt: c.updated_at || null,
  }))
  const costEstTotal = taskCosts.reduce((s, c) => s + c.estimated, 0)
  const costActTotal = taskCosts.reduce((s, c) => s + c.actual, 0)
  return {
    id: t.id, supabaseId: t.id, name: t.name,
    status: t.status || "Not Started",
    startDate: t.start_date || "", dueDate: t.due_date || "",
    dateCompleted: t.date_completed || "",
    assignedTo: t.assigned_to || "", assignedUserId: t.assigned_user_id || null,
    notes: t.notes || "", rejectionNote: t.rejection_note || "",
    estimatedCost: taskCosts.length > 0 ? costEstTotal : (t.estimated_cost != null ? Number(t.estimated_cost) : null),
    actualCost: taskCosts.length > 0 ? costActTotal : (t.actual_cost != null ? Number(t.actual_cost) : null),
    costs: taskCosts,
    durationDays: t.duration_days ?? null,
    predecessors: t.predecessors || [],
    isPunchList: t.is_punch_list || false,
    parentId: t.parent_id || null,
    isPhaseHeader: t.is_phase_header || false,
    updatedAt: t.updated_at || null,
  }
}

// Stakeholder-safe task mapping — no financial data
export function mapTaskStakeholder(t) {
  return {
    id: t.id, supabaseId: t.id, name: t.name,
    status: t.status || "Not Started",
    startDate: t.start_date || "", dueDate: t.due_date || "",
    dateCompleted: t.date_completed || "",
    assignedTo: t.assigned_to || "", assignedUserId: t.assigned_user_id || null,
    notes: "", rejectionNote: "",
    estimatedCost: null, actualCost: null, costs: [],
    durationDays: t.duration_days ?? null,
    predecessors: t.predecessors || [],
    isPunchList: t.is_punch_list || false,
    parentId: t.parent_id || null,
    isPhaseHeader: t.is_phase_header || false,
    updatedAt: t.updated_at || null,
  }
}

// T08 note: safe for stakeholder viewers even without financial stripping.
// Realtime payloads patch per-task fields in place and never reconstruct
// project-level rollups. Stakeholder masking lives on the refetch path
// (see `mapTaskStakeholder` above), which is the only code path that
// rebuilds a full project shape from the server.
export function mapRealtimeTask(t) {
  return {
    id: t.id, supabaseId: t.id, name: t.name,
    status: t.status || "Not Started",
    startDate: t.start_date || "", dueDate: t.due_date || "",
    dateCompleted: t.date_completed || "",
    assignedTo: t.assigned_to || "", assignedUserId: t.assigned_user_id || null,
    notes: t.notes || "", rejectionNote: t.rejection_note || "",
    estimatedCost: t.estimated_cost != null ? Number(t.estimated_cost) : null,
    actualCost: t.actual_cost != null ? Number(t.actual_cost) : null,
    costs: [],
    durationDays: t.duration_days ?? null,
    predecessors: t.predecessors || [],
    isPunchList: t.is_punch_list || false,
    parentId: t.parent_id || null,
    isPhaseHeader: t.is_phase_header || false,
    updatedAt: t.updated_at || null,
  }
}

function mapChangeOrder(co) {
  return {
    id: co.id, supabaseId: co.id, projectId: co.project_id,
    taskId: co.task_id, coNumber: co.co_number,
    title: co.title || "", description: co.description || "",
    reason: co.reason || "scope_change",
    originalAmount: Number(co.original_amount) || 0,
    newAmount: Number(co.new_amount) || 0,
    delta: Number(co.delta) || 0,
    status: co.status || "draft",
    createdBy: co.created_by, createdByName: co.created_by_name || "",
    approvedBy: co.approved_by, approvedByName: co.approved_by_name || "",
    approvedAt: co.approved_at, rejectionNote: co.rejection_note || "",
    notes: co.notes || "", createdAt: co.created_at,
    updatedAt: co.updated_at || null,
  }
}

function mapNote(n) {
  return {
    id: n.id, supabaseId: n.id, text: n.text,
    ts: new Date(n.created_at).toLocaleString(),
    actorName: n.actor_name || null,
    actorId: n.actor_id || null,
    eventType: n.event_type || "note",
  }
}

export function useProjects(session, showToast, { activeProjectId, setActivatingInvite, reloadFlag, userSettings, setContacts, saveContactToSupabase, getUserDisplayName }) {
  const [projects, setProjects] = useState([])
  const [projectsLoaded, setProjectsLoaded] = useState(false)

  // --- Load projects ---
  useEffect(() => {
    if (!session) return
    async function loadProjects() {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      // Single RPC call — SECURITY DEFINER, bypasses RLS chain
      const { data: rpcResult, error: rpcError } = await supabase.rpc('get_my_projects')
      if (rpcError) {
        console.error('Failed to load projects:', rpcError)
        setProjects([]); setProjectsLoaded(true); return
      }
      const entries = Array.isArray(rpcResult) ? rpcResult : []
      if (entries.length === 0) { setProjects([]); setProjectsLoaded(true); return }
      const projData = entries.map(e => e.project)
      const memberData = entries.map(e => ({ project_id: e.project.id, role: e.role }))
      const projectIds = projData.map(p => p.id)
      // Split by role — stakeholders don't get financial data
      const stakeholderIds = memberData.filter(m => m.role === "stakeholder").map(m => m.project_id)
      const fullAccessIds = projectIds.filter(id => !stakeholderIds.includes(id))
      // Fetch tasks for all projects, but costs/COs only for non-stakeholder projects
      const queries = [
        supabase.from("tasks").select("*").in("project_id", projectIds).order("sort_order"),
        supabase.from("notes").select("*").in("project_id", projectIds).order("created_at", { ascending: false }),
      ]
      if (fullAccessIds.length > 0) {
        queries.push(supabase.from("task_costs").select("*").in("project_id", fullAccessIds))
        queries.push(supabase.from("change_orders").select("*").in("project_id", fullAccessIds).order("created_at"))
      }
      const results = await Promise.all(queries)
      const taskData = results[0].data, noteData = results[1].data
      const costData = fullAccessIds.length > 0 ? results[2].data : []
      const coData = fullAccessIds.length > 0 ? results[3].data : []
      const mapped = projData.map(p => {
        const role = (memberData || []).find(m => m.project_id === p.id)?.role || "admin"
        const isStakeholder = role === "stakeholder"
        return {
          id: p.id, supabaseId: p.id, role,
          name: p.name, type: p.type, subtype: p.subtype || "spec",
          address: p.address || "", start: p.start || "", target: p.target || "",
          createdAt: p.created_at ? new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "",
          sellerContactId: p.seller_contact_id || null,
          legalDescription: p.legal_description || "",
          client: isStakeholder ? "" : (p.client || ""), clientPhone: isStakeholder ? "" : (p.client_phone || ""),
          clientEmail: isStakeholder ? "" : (p.client_email || ""),
          buyerContactId: p.buyer_contact_id || null,
          groupId: p.group_id || null,
          contractPrice: isStakeholder ? null : (p.contract_price != null ? Number(p.contract_price) : null),
          updatedAt: p.updated_at || null,
          tasks: (taskData || []).filter(t => t.project_id === p.id).map(t =>
            isStakeholder ? mapTaskStakeholder(t) : mapTask(t, costData)
          ),
          notes: isStakeholder ? [] : (noteData || []).filter(n => n.project_id === p.id).map(mapNote),
          changeOrders: isStakeholder ? [] : (coData || []).filter(co => co.project_id === p.id).map(mapChangeOrder),
        }
      })
      setProjects(mapped)
      setProjectsLoaded(true)
    }
    loadProjects().then(() => { if (reloadFlag > 0) setActivatingInvite(false) })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stabilize: only re-fetch on user change, not token refresh
  }, [session?.user?.id, reloadFlag, setActivatingInvite])

  // --- Refetch a single project (used on conflict detection) ---
  async function refetchProject(supabaseId) {
    const [projRes, taskRes, costRes, coRes, noteRes] = await Promise.all([
      supabase.from("projects").select("*").eq("id", supabaseId).single(),
      supabase.from("tasks").select("*").eq("project_id", supabaseId).order("sort_order"),
      supabase.from("task_costs").select("*").eq("project_id", supabaseId),
      supabase.from("change_orders").select("*").eq("project_id", supabaseId).order("created_at"),
      supabase.from("notes").select("*").eq("project_id", supabaseId).order("created_at", { ascending: false }),
    ])
    if (!projRes.data) return
    const p = projRes.data
    setProjects(ps => ps.map(existing => {
      if (existing.supabaseId !== supabaseId) return existing
      const isStakeholder = existing.role === "stakeholder"
      return {
        ...existing,
        name: p.name, type: p.type, subtype: p.subtype || "spec",
        address: p.address || "", start: p.start || "", target: p.target || "",
        sellerContactId: p.seller_contact_id || null,
        legalDescription: p.legal_description || "",
        client: isStakeholder ? "" : (p.client || ""), clientPhone: isStakeholder ? "" : (p.client_phone || ""),
        clientEmail: isStakeholder ? "" : (p.client_email || ""),
        buyerContactId: p.buyer_contact_id || null,
        groupId: p.group_id || null,
        contractPrice: isStakeholder ? null : (p.contract_price != null ? Number(p.contract_price) : null),
        updatedAt: p.updated_at || null,
        tasks: (taskRes.data || []).map(t => isStakeholder ? mapTaskStakeholder(t) : mapTask(t, costRes.data)),
        changeOrders: isStakeholder ? [] : (coRes.data || []).map(mapChangeOrder),
        notes: isStakeholder ? [] : (noteRes.data || []).map(mapNote),
      }
    }))
  }

  // --- Realtime subscription for active project ---
  // Legacy activeId state preserved for React Compiler hook-count stability.
  // URL-based activeProjectId (from useRouter) takes precedence via resolvedActiveId.
  // sessionStorage read kept in initializer for compiler compatibility; writes removed (URL is source of truth).
  const [activeId, _setActiveId] = useState(() => sessionStorage.getItem('baulit_activeId') || null)
  function setActiveId(id) {
    const val = typeof id === 'function' ? id(activeId) : id;
    _setActiveId(val);
  }

  // Derive active project from URL param when provided, falling back to legacy activeId
  const resolvedActiveId = activeProjectId !== undefined && activeProjectId !== null ? activeProjectId : activeId
  const activeSupabaseId = projects.find(p => p.id === resolvedActiveId)?.supabaseId || null
  const activeProject = projects.find(p => p.id === resolvedActiveId) || null

  useEffect(() => {
    if (!session || !activeSupabaseId) return
    const pid = activeSupabaseId
    const channel = supabase
      .channel(`project-${pid}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter: `project_id=eq.${pid}` }, (payload) => {
        if (payload.eventType === 'INSERT') {
          const mapped = mapRealtimeTask(payload.new)
          setProjects(ps => ps.map(p => {
            if (p.supabaseId !== pid) return p
            if (p.tasks.some(t => t.supabaseId === mapped.supabaseId)) return p
            return { ...p, tasks: [...p.tasks, mapped] }
          }))
        } else if (payload.eventType === 'UPDATE') {
          const mapped = mapRealtimeTask(payload.new)
          setProjects(ps => ps.map(p => {
            if (p.supabaseId !== pid) return p
            const exists = p.tasks.some(t => t.supabaseId === mapped.supabaseId)
            if (!exists) return { ...p, tasks: [...p.tasks, mapped] }
            return { ...p, tasks: p.tasks.map(t =>
              t.supabaseId === mapped.supabaseId ? { ...mapped, costs: t.costs, estimatedCost: t.costs?.length > 0 ? t.estimatedCost : mapped.estimatedCost, actualCost: t.costs?.length > 0 ? t.actualCost : mapped.actualCost } : t
            )}
          }))
        } else if (payload.eventType === 'DELETE') {
          const oldId = payload.old.id
          setProjects(ps => ps.map(p => {
            if (p.supabaseId !== pid) return p
            return { ...p, tasks: p.tasks.filter(t => t.supabaseId !== oldId) }
          }))
        }
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notes', filter: `project_id=eq.${pid}` }, (payload) => {
        if (payload.eventType === 'INSERT') {
          const n = payload.new
          const mapped = mapNote(n)
          setProjects(ps => ps.map(p => {
            if (p.supabaseId !== pid) return p
            if ((p.notes || []).some(x => x.supabaseId === n.id)) return p
            return { ...p, notes: [mapped, ...(p.notes || [])] }
          }))
        } else if (payload.eventType === 'DELETE') {
          const oldId = payload.old.id
          setProjects(ps => ps.map(p => {
            if (p.supabaseId !== pid) return p
            return { ...p, notes: (p.notes || []).filter(n => n.supabaseId !== oldId) }
          }))
        }
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'change_orders', filter: `project_id=eq.${pid}` }, (payload) => {
        if (payload.eventType === 'INSERT') {
          const mapped = mapChangeOrder(payload.new)
          setProjects(ps => ps.map(p => {
            if (p.supabaseId !== pid) return p
            if ((p.changeOrders || []).some(co => co.supabaseId === mapped.supabaseId)) return p
            return { ...p, changeOrders: [...(p.changeOrders || []), mapped] }
          }))
        } else if (payload.eventType === 'UPDATE') {
          const mapped = mapChangeOrder(payload.new)
          setProjects(ps => ps.map(p => {
            if (p.supabaseId !== pid) return p
            return { ...p, changeOrders: (p.changeOrders || []).map(co =>
              co.supabaseId === mapped.supabaseId ? mapped : co
            )}
          }))
        } else if (payload.eventType === 'DELETE') {
          const oldId = payload.old.id
          setProjects(ps => ps.map(p => {
            if (p.supabaseId !== pid) return p
            return { ...p, changeOrders: (p.changeOrders || []).filter(co => co.supabaseId !== oldId) }
          }))
        }
      })
      .subscribe((status, err) => {
        if (status === 'CHANNEL_ERROR') {
          console.error('Realtime channel error:', err)
        } else if (status === 'TIMED_OUT') {
          console.warn('Realtime channel timed out, re-subscribing...')
          channel.subscribe()
        }
      })
    return () => { supabase.removeChannel(channel) }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stabilize: only re-subscribe on user change, not token refresh
  }, [session?.user?.id, activeSupabaseId])

  // --- Project CRUD ---
  async function saveProjectToSupabase(project) {
    const { data: { user } } = await supabase.auth.getUser()
    const row = {
      user_id: user.id, name: project.name, type: project.type,
      subtype: project.subtype || null, address: project.address || null,
      start: project.start || null, target: project.target || null,
      seller_contact_id: project.sellerContactId || null,
      legal_description: project.legalDescription || null,
      client: project.client || null, client_phone: project.clientPhone || null,
      client_email: project.clientEmail || null,
      buyer_contact_id: project.buyerContactId || null,
      contract_price: project.contractPrice ?? null,
    }
    if (project.supabaseId) {
      const result = await conflictCheckedUpdate(supabase, "projects", project.supabaseId, row, project.updatedAt)
      if (result.conflict) return { conflict: true }
      if (!result.ok) { console.error("Failed to update project:", result.error); showToast("Failed to save project. Please try again.", "error"); return { ok: false }; }
      return { ok: true, updatedAt: result.data?.updated_at }
    } else {
      const { data, error } = await supabase.from("projects").insert(row).select().single()
      if (error) { console.error("Failed to create project:", error); showToast("Failed to create project. Please try again.", "error"); return null; }
      return data.id
    }
  }

  async function cleanupAttachments(filter) {
    try {
      const { data: rows } = await supabase.from("attachments").select("id, storage_path").match(filter)
      if (!rows || rows.length === 0) return
      const paths = rows.map(r => r.storage_path).filter(Boolean)
      if (paths.length > 0) {
        await supabase.storage.from("attachments").remove(paths)
      }
      await supabase.from("attachments").delete().in("id", rows.map(r => r.id))
    } catch (err) {
      console.error("Attachment cleanup failed:", err)
    }
  }

  async function deleteProjectFromSupabase(supabaseId) {
    await cleanupAttachments({ project_id: supabaseId })
    const { error } = await supabase.from("projects").delete().eq("id", supabaseId)
    if (error) { console.error("Failed to delete project:", error); showToast("Failed to delete project. Please try again.", "error"); }
  }

  // --- Task CRUD ---
  async function addTaskToSupabase(projectId, task, sortOrder) {
    const { data, error } = await supabase.from("tasks").insert({
      project_id: projectId, name: task.name, status: task.status,
      start_date: task.startDate || null, due_date: task.dueDate || null,
      date_completed: task.dateCompleted || null, assigned_to: task.assignedTo || null,
      notes: task.notes || null,
      estimated_cost: task.estimatedCost != null ? task.estimatedCost : null,
      actual_cost: task.actualCost != null ? task.actualCost : null,
      duration_days: task.durationDays ?? null, predecessors: task.predecessors || [],
      is_punch_list: task.isPunchList || false,
      parent_id: task.parentId || null, is_phase_header: task.isPhaseHeader || false,
      sort_order: sortOrder,
    }).select().single()
    if (error) { console.error("Failed to add task:", error); showToast("Failed to add task. Please try again.", "error"); return null; }
    return data.id
  }

  async function deleteTaskFromSupabase(taskSupabaseId) {
    await cleanupAttachments({ task_id: taskSupabaseId })
    const { error } = await supabase.from("tasks").delete().eq("id", taskSupabaseId)
    if (error) { console.error("Failed to delete task:", error); showToast("Failed to delete task. Please try again.", "error"); }
  }

  // --- Cost line CRUD ---
  async function addTaskCost(projectId, taskId, costLine) {
    const { data, error } = await supabase.from("task_costs").insert({
      task_id: taskId, project_id: projectId,
      category: costLine.category || "Other", description: costLine.description || "",
      estimated: costLine.estimated ?? 0, actual: costLine.actual ?? 0,
    }).select().single()
    if (error) { console.error("Failed to add cost line:", error); return null; }
    return { id: data.id, supabaseId: data.id, taskId: data.task_id, projectId: data.project_id, category: data.category, description: data.description || "", estimated: Number(data.estimated) || 0, actual: Number(data.actual) || 0 }
  }

  async function updateTaskCost(costId, changes, previousUpdatedAt) {
    const payload = {}
    if (changes.category !== undefined) payload.category = changes.category
    if (changes.description !== undefined) payload.description = changes.description
    if (changes.estimated !== undefined) payload.estimated = changes.estimated ?? 0
    if (changes.actual !== undefined) payload.actual = changes.actual ?? 0
    const result = await conflictCheckedUpdate(supabase, "task_costs", costId, payload, previousUpdatedAt)
    if (result.conflict) { showToast("Cost data was modified by another user. Please refresh and try again.", "warning"); return { conflict: true } }
    if (!result.ok) { console.error("Failed to update cost line:", result.error); return null }
    return { updatedAt: result.data.updated_at }
  }

  async function deleteTaskCost(costId) {
    const { error } = await supabase.from("task_costs").delete().eq("id", costId)
    if (error) console.error("Failed to delete cost line:", error)
  }

  // --- Change Order CRUD ---
  async function addChangeOrder(projectId, co) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null
    const { data: row, error } = await supabase.from("change_orders").insert({
      project_id: projectId, task_id: co.taskId || null, co_number: co.coNumber,
      title: co.title, description: co.description || "", reason: co.reason || "scope_change",
      original_amount: co.originalAmount ?? 0, new_amount: co.newAmount ?? 0,
      status: "draft", created_by: user.id, created_by_name: getUserDisplayName(),
      notes: co.notes || "",
    }).select().single()
    if (error) { console.error("Failed to add change order:", error); showToast("Failed to create change order.", "error"); return null }
    const mapped = mapChangeOrder(row)
    setProjects(ps => ps.map(p => p.id === projectId ? { ...p, changeOrders: [...(p.changeOrders || []), mapped] } : p))
    trackEvent('change_order_created')
    return mapped
  }

  async function updateChangeOrder(projectId, coId, changes) {
    const payload = {}
    if (changes.title !== undefined) payload.title = changes.title
    if (changes.description !== undefined) payload.description = changes.description
    if (changes.reason !== undefined) payload.reason = changes.reason
    if (changes.taskId !== undefined) payload.task_id = changes.taskId || null
    if (changes.originalAmount !== undefined) payload.original_amount = changes.originalAmount ?? 0
    if (changes.newAmount !== undefined) payload.new_amount = changes.newAmount ?? 0
    if (changes.status !== undefined) payload.status = changes.status
    if (changes.notes !== undefined) payload.notes = changes.notes
    if (changes.rejectionNote !== undefined) payload.rejection_note = changes.rejectionNote
    if (changes.approvedBy !== undefined) payload.approved_by = changes.approvedBy
    if (changes.approvedByName !== undefined) payload.approved_by_name = changes.approvedByName
    if (changes.approvedAt !== undefined) payload.approved_at = changes.approvedAt
    const existingCO = projects.find(p => p.id === projectId)?.changeOrders?.find(co => co.id === coId)
    const result = await conflictCheckedUpdate(supabase, "change_orders", coId, payload, existingCO?.updatedAt)
    if (result.conflict) {
      showToast("Change order was modified by another user. Refreshing…", "warning")
      const { data: freshCOs } = await supabase.from("change_orders").select("*").eq("project_id", projectId).order("created_at")
      if (freshCOs) setProjects(ps => ps.map(p => p.id === projectId ? { ...p, changeOrders: freshCOs.map(mapChangeOrder) } : p))
      return null
    }
    if (!result.ok) { console.error("Failed to update change order:", result.error); return null }
    const mapped = mapChangeOrder(result.data)
    setProjects(ps => ps.map(p => p.id === projectId ? { ...p, changeOrders: (p.changeOrders || []).map(co => co.id === coId ? mapped : co) } : p))
    return mapped
  }

  async function deleteChangeOrder(projectId, coId) {
    await cleanupAttachments({ task_id: coId })
    const { error } = await supabase.from("change_orders").delete().eq("id", coId)
    if (error) { console.error("Failed to delete change order:", error); return }
    setProjects(ps => ps.map(p => p.id === projectId ? { ...p, changeOrders: (p.changeOrders || []).filter(co => co.id !== coId) } : p))
  }

  async function approveChangeOrder(projectId, coId, approve, rejectionNote) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    // Rejection is a single update — no transaction needed
    if (!approve) {
      const updated = await updateChangeOrder(projectId, coId, { status: "rejected", rejectionNote: rejectionNote || "" })
      return updated
    }
    // Approval: use transactional RPC to atomically approve + create cost line
    const project = projects.find(p => p.id === projectId)
    const co = project ? (project.changeOrders || []).find(c => c.id === coId) : null
    const { data: result, error: rpcError } = await supabase.rpc('approve_change_order_with_cost', {
      p_co_id: coId,
      p_approved_by_name: getUserDisplayName(),
    })
    if (rpcError || !result) {
      console.error("Failed to approve change order:", rpcError)
      showToast("Failed to approve change order. Please try again.", "error")
      return null
    }
    const mapped = mapChangeOrder(result.change_order)
    setProjects(ps => ps.map(p => p.id === projectId ? { ...p, changeOrders: (p.changeOrders || []).map(c => c.id === coId ? mapped : c) } : p))
    // If a cost line was created, update the task's costs in state
    if (result.cost_line && co && co.taskId) {
      const cl = result.cost_line
      const costLine = { id: cl.id, supabaseId: cl.id, taskId: cl.task_id, projectId: cl.project_id, category: cl.category, description: cl.description || "", estimated: Number(cl.estimated) || 0, actual: Number(cl.actual) || 0 }
      const task = (project.tasks || []).find(t => t.id === co.taskId || t.supabaseId === co.taskId)
      if (task) {
        setProjects(ps => ps.map(p => p.id === projectId ? {
          ...p, tasks: p.tasks.map(t => {
            if (t.id !== task.id && t.supabaseId !== task.supabaseId) return t
            const updatedCosts = [...(t.costs || []), costLine]
            return { ...t, costs: updatedCosts, estimatedCost: updatedCosts.reduce((s, c) => s + (c.estimated || 0), 0), actualCost: updatedCosts.reduce((s, c) => s + (c.actual || 0), 0) }
          })
        } : p))
      }
    }
    return mapped
  }

  // --- High-level project operations ---
  async function createProject(data, newContact, selectedTemplate) {
    const tier = userSettings?.subscriptionTier || "pro";
    const limits = getTierLimits(tier);
    // Only count admin-owned projects toward tier limits (not contractor/stakeholder invitations)
    const activeProjects = projects.filter(p => !p.archived && (!p.role || p.role === 'admin'));
    if (activeProjects.length >= limits.projects) {
      showToast(`You've reached the ${limits.projects}-project limit on your ${tier === "free" ? "Free" : tier === "pro" ? "Pro" : "Enterprise"} plan. Upgrade to add more projects.`, "warning");
      return;
    }
    let templateTasks = null;
    let tasks;
    if (selectedTemplate && selectedTemplate.tasks && selectedTemplate.tasks.length > 0) {
      templateTasks = selectedTemplate.tasks;
      tasks = makeTasksFromTemplate(templateTasks);
    } else {
      const defaultTemplate = data.type === "land" ? LOT_ACQUISITION_TEMPLATE : data.subtype === "custom" ? CUSTOM_HOME_TEMPLATE : SPEC_BUILD_TEMPLATE;
      templateTasks = defaultTemplate;
      tasks = makeTasksFromTemplate(defaultTemplate);
    }
    let sellerContactId = data.sellerContactId || null
    if (newContact) {
      const newSupabaseId = await saveContactToSupabase(newContact)
      const savedContact = { ...newContact, id: newSupabaseId, supabaseId: newSupabaseId }
      setContacts(cs => [...cs, savedContact])
      sellerContactId = newSupabaseId
    }
    // Build task payload with template refs for server-side ID mapping
    const taskPayload = tasks.map((t, i) => ({
      name: t.name, status: t.status,
      startDate: t.startDate || null, dueDate: t.dueDate || null,
      dateCompleted: t.dateCompleted || null, assignedTo: t.assignedTo || null,
      notes: t.notes || null,
      estimatedCost: t.estimatedCost != null ? t.estimatedCost : null,
      actualCost: t.actualCost != null ? t.actualCost : null,
      durationDays: t.durationDays ?? null, isPhaseHeader: t.isPhaseHeader || false,
      refId: templateTasks ? templateTasks[i].refId : `task-${i}`,
      parentRefId: templateTasks ? (templateTasks[i].parentRefId || null) : null,
      predecessors: templateTasks ? (templateTasks[i].predecessors || []) : [],
    }))
    const { data: result, error: rpcError } = await supabase.rpc('create_project_with_tasks', {
      p_name: data.name,
      p_type: data.type,
      p_subtype: data.subtype || null,
      p_address: data.address || null,
      p_start: data.start || null,
      p_target: data.target || null,
      p_seller_contact_id: sellerContactId,
      p_legal_description: data.legalDescription || null,
      p_client: data.client || null,
      p_client_phone: data.clientPhone || null,
      p_client_email: data.clientEmail || null,
      p_buyer_contact_id: data.buyerContactId || null,
      p_contract_price: data.contractPrice ?? null,
      p_tasks: taskPayload,
    })
    if (rpcError || !result) {
      console.error("Failed to create project:", rpcError)
      showToast("Failed to create project. Please try again.", "error")
      return
    }
    const supabaseId = result.project_id
    const tasksWithIds = (result.tasks || []).map(mapRealtimeTask)
    const proj = { ...data, sellerContactId, tasks: tasksWithIds, notes: [], createdAt: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) }
    const fullProj = { ...proj, id: supabaseId, supabaseId }
    setProjects(p => [...p, fullProj])
    trackEvent('project_created', { type: data.type, template: selectedTemplate?.name || 'blank' })
    setActiveId(supabaseId)
    return supabaseId
  }

  function updateProject(id, updater) {
    let prev, updated;
    setProjects(ps => {
      prev = ps.find(p => p.id === id)
      const next = ps.map(p => p.id === id ? updater(p) : p)
      updated = next.find(p => p.id === id)
      return next
    })
    queueMicrotask(() => {
      if (!updated || !updated.supabaseId) return
      // Skip the projects-row write when only child collections (tasks, notes, etc.)
      // changed. A redundant UPDATE bumps projects.updated_at and races against
      // any in-flight save that captured the earlier timestamp, causing the
      // false "Project was modified by another user" toast on rapid task edits.
      const projectFieldsChanged = !prev ||
        prev.name !== updated.name ||
        prev.type !== updated.type ||
        prev.subtype !== updated.subtype ||
        prev.address !== updated.address ||
        prev.start !== updated.start ||
        prev.target !== updated.target ||
        prev.sellerContactId !== updated.sellerContactId ||
        prev.legalDescription !== updated.legalDescription ||
        prev.client !== updated.client ||
        prev.clientPhone !== updated.clientPhone ||
        prev.clientEmail !== updated.clientEmail ||
        prev.buyerContactId !== updated.buyerContactId ||
        prev.contractPrice !== updated.contractPrice
      const projectSavePromise = projectFieldsChanged
        ? saveProjectToSupabase(updated)
        : Promise.resolve({ ok: true, skipped: true })
      projectSavePromise.then(result => {
        if (result && result.conflict) {
          showToast("Project was modified by another user. Refreshing…", "warning")
          refetchProject(updated.supabaseId)
          return
        }
        if (result && result.ok === false) {
          refetchProject(updated.supabaseId)
          return
        }
        if (result && result.ok && !result.skipped && result.updatedAt) {
          // Sync updated_at back to React state so subsequent rapid edits
          // don't false-conflict against the stale timestamp
          setProjects(ps => ps.map(p =>
            p.supabaseId === updated.supabaseId ? { ...p, updatedAt: result.updatedAt } : p
          ))
        }
        if (prev && updated.tasks) {
          updated.tasks.forEach((t, i) => {
            if (!t.supabaseId) return
            const old = prev.tasks.find(ot => ot.id === t.id)
            const orderChanged = prev.tasks.findIndex(ot => ot.id === t.id) !== i
            if (orderChanged) {
              supabase.from("tasks").update({ sort_order: i }).eq("id", t.supabaseId).then(({ error }) => { if (error) showToast("Failed to save task order.", "error") })
            }
            if (!old || old.status !== t.status || old.startDate !== t.startDate || old.dueDate !== t.dueDate || old.dateCompleted !== t.dateCompleted || old.assignedTo !== t.assignedTo || old.assignedUserId !== t.assignedUserId || old.notes !== t.notes || old.name !== t.name || old.rejectionNote !== t.rejectionNote || old.durationDays !== t.durationDays || JSON.stringify(old.predecessors) !== JSON.stringify(t.predecessors) || old.isPunchList !== t.isPunchList || old.parentId !== t.parentId || old.isPhaseHeader !== t.isPhaseHeader) {
              conflictCheckedUpdate(supabase, "tasks", t.supabaseId, {
                name: t.name, status: t.status,
                start_date: t.startDate || null, due_date: t.dueDate || null,
                date_completed: t.dateCompleted || null, assigned_to: t.assignedTo || null,
                assigned_user_id: t.assignedUserId || null, notes: t.notes || null,
                rejection_note: t.rejectionNote || null, duration_days: t.durationDays ?? null,
                predecessors: t.predecessors || [], is_punch_list: t.isPunchList || false,
                parent_id: t.parentId || null, is_phase_header: t.isPhaseHeader || false,
              }, t.updatedAt).then(result => {
                if (result.conflict) {
                  showToast("A task was modified by another user. Refreshing…", "warning")
                  refetchProject(updated.supabaseId)
                } else if (!result.ok) {
                  showToast("Failed to save task changes. Refreshing…", "error")
                  refetchProject(updated.supabaseId)
                } else if (result.data && result.data.updated_at) {
                  // Sync updated_at back to React state so subsequent rapid edits
                  // don't false-conflict against the stale timestamp
                  setProjects(ps => ps.map(p => {
                    if (p.supabaseId !== updated.supabaseId) return p
                    return { ...p, tasks: p.tasks.map(tk =>
                      tk.supabaseId === t.supabaseId ? { ...tk, updatedAt: result.data.updated_at } : tk
                    )}
                  }))
                }
              })
            }
          })
          const prevIds = new Set(prev.tasks.map(t => t.supabaseId).filter(Boolean))
          const nextIds = new Set(updated.tasks.map(t => t.supabaseId).filter(Boolean))
          prevIds.forEach(sid => { if (!nextIds.has(sid)) deleteTaskFromSupabase(sid) })
        }
      }).catch(err => {
        console.error("Failed to save project:", err)
        showToast("Failed to save project. Refreshing…", "error")
        refetchProject(updated.supabaseId)
      })
    })
  }

  async function deleteProject(id) {
    const project = projects.find(p => p.id === id)
    if (project && project.supabaseId) await deleteProjectFromSupabase(project.supabaseId)
    setProjects(ps => ps.filter(p => p.id !== id))
    setActiveId(null)
  }

  async function archiveProject(id) {
    const project = projects.find(p => p.id === id)
    if (project && project.supabaseId) {
      const { error } = await supabase.from("projects").update({ archived: true }).eq("id", project.supabaseId)
      if (error) { console.error("Failed to archive project:", error); return }
    }
    setProjects(ps => ps.filter(p => p.id !== id))
    setActiveId(null)
  }

  async function unarchiveProject(supabaseId) {
    if (!supabaseId) return
    const { error } = await supabase.from("projects").update({ archived: false }).eq("id", supabaseId)
    if (error) { console.error("Failed to unarchive project:", error) }
  }

  async function deleteArchivedProject(supabaseId) {
    await deleteProjectFromSupabase(supabaseId)
  }

  // Update React state only — no Supabase save.
  // Use for notes/change-order optimistic updates where the DB write is already done separately.
  function updateProjectLocal(id, updater) {
    setProjects(ps => ps.map(p => p.id === id ? updater(p) : p))
  }

  function handleImport(data) {
    setProjects(data.projects)
    setContacts(data.contacts)
  }

  function reset() {
    setProjects([])
    setProjectsLoaded(false)
    _setActiveId(null)
  }

  return {
    projects, setProjects,
    projectsLoaded,
    activeId, setActiveId,
    activeProject,
    createProject, updateProject, updateProjectLocal, deleteProject,
    archiveProject, unarchiveProject, deleteArchivedProject,
    addTaskToSupabase, deleteTaskFromSupabase,
    addTaskCost, updateTaskCost, deleteTaskCost,
    addChangeOrder, updateChangeOrder, deleteChangeOrder, approveChangeOrder,
    handleImport,
    reset,
  }
}
